import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import type { School, Grade, ReportedIssue } from '@/types';

const statusColors = {
  new: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  'in review': 'bg-yellow-50 text-yellow-700 ring-yellow-700/10',
  'on hold': 'bg-orange-50 text-orange-700 ring-orange-700/10',
  resolved: 'bg-green-50 text-green-700 ring-green-700/10',
  rejected: 'bg-red-50 text-red-700 ring-red-700/10',
} as const;

export default function EditIssue() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issue, setIssue] = useState<ReportedIssue | null>(null);
  const [status, setStatus] = useState<ReportedIssue['status']>('new');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    if (id) {
      fetchIssue(id);
    }
  }, [id]);

  const fetchIssue = async (issueId: string) => {
    try {
      const { data, error } = await supabase
        .from('sms_reported_issues')
        .select(`
          *,
          school:sms_schools(name),
          grade:sms_grades(name)
        `)
        .eq('id', issueId)
        .single();

      if (error) throw error;
      if (!data) {
        toast.error('Issue not found');
        navigate('/admin/reported-issues');
        return;
      }

      setIssue(data);
      setStatus(data.status);
      setRemarks(data.remarks || '');
    } catch (error) {
      console.error('Error fetching issue:', error);
      toast.error('Failed to fetch issue');
      navigate('/admin/reported-issues');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !issue) return;

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('sms_reported_issues')
        .update({
          status,
          remarks,
        })
        .eq('id', id);

      if (error) throw error;

      toast.success('Issue updated successfully');
      navigate('/admin/reported-issues');
    } catch (error) {
      console.error('Error updating issue:', error);
      toast.error('Failed to update issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !issue) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container max-w-2xl mx-auto py-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Issue #{issue.issue_number}</h1>
          <Badge
            variant="outline"
            className={`${statusColors[status as keyof typeof statusColors]}`}
          >
            {status}
          </Badge>
        </div>
        <p className="text-muted-foreground mt-2">
          Update the issue status and add remarks
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>School Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">School</label>
              <Input
                value={issue.school?.name}
                disabled
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Grade</label>
              <Input
                value={issue.grade?.name}
                disabled
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Teacher Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Teacher Name</label>
              <Input
                value={issue.teacher_name}
                disabled
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Contact Number</label>
              <Input
                value={issue.contact_number || ''}
                disabled
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Subject Not Being Taught</label>
              <Input
                value={issue.subject}
                disabled
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Issue Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Details of Concern</label>
              <Textarea
                value={issue.details}
                rows={4}
                disabled
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={status}
                onValueChange={(value: ReportedIssue['status']) => setStatus(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in review">In Review</SelectItem>
                  <SelectItem value="on hold">On Hold</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Remarks</label>
              <Textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add any remarks or notes about this issue"
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/reported-issues')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}